import { Form } from 'antd';
import React, { useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styled from 'styled-components';

const QuillComponent = ReactQuill as unknown as React.ComponentType<any>;

interface IProps {
  input: any;
  idComponent?: string;
  placeholder?: string;
  isMounted?: boolean;
  meta: any;
  initValues?: any;
  label?: string;
  disabled?: boolean;
  className?: string;
  editMode?: boolean;
}

interface IWrapperProps {
  hasError?: boolean;
  disabled?: boolean;
}

const QuillWrapper = styled.div<IWrapperProps>`
  .quill {
    display: flex;
    flex-direction: column;
  }

  /* Toolbar styling */
  .ql-toolbar.ql-snow {
    border-color: ${(props) => (props.hasError ? '#ff4d4f' : '#d9d9d9')};
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    background-color: #f7f7f7;
    padding: 8px 12px;
    transition: all 0.3s;

    ${(props) =>
      props.disabled &&
      `
      opacity: 0.6;
      pointer-events: none;
      background-color: #f5f5f5;
    `}
  }

  /* Content area styling */
  .ql-container.ql-snow {
    border-color: ${(props) => (props.hasError ? '#ff4d4f' : '#d9d9d9')};
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    min-height: 180px;
    font-size: 14px;
    font-family: inherit;
    background-color: ${(props) => (props.disabled ? '#f5f5f5' : '#ffffff')};
    transition: all 0.3s;
  }

  .ql-editor {
    min-height: 160px;
    font-size: 14px;
    line-height: 1.6;
    color: ${(props) => (props.disabled ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.85)')};

    ${(props) =>
      props.disabled &&
      `
      cursor: not-allowed;
    `}

    &.ql-blank::before {
      font-style: normal;
      color: #bfbfbf;
    }
  }

  /* Hover and focus states */
  &:hover .ql-toolbar.ql-snow,
  &:hover .ql-container.ql-snow {
    border-color: ${(props) => (props.hasError ? '#ff4d4f' : '#40a9ff')};
  }

  .quill:focus-within .ql-toolbar.ql-snow,
  .quill:focus-within .ql-container.ql-snow {
    border-color: ${(props) => (props.hasError ? '#ff4d4f' : '#40a9ff')};
  }
`;

export const InputRichText = (props: IProps) => {
  const {
    input,
    meta: { error, touched },
    idComponent,
    placeholder,
    label,
    disabled = false,
    className = '',
    initValues,
  } = props;

  const value = input?.value !== undefined && input?.value !== '' ? input.value : initValues || '';

  // Rich text toolbar modules
  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['link', 'blockquote', 'code-block'],
        ['clean'],
      ],
    }),
    [],
  );

  const formats = [
    'header',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'list',
    'indent',
    'align',
    'link',
    'blockquote',
    'code-block',
  ];

  const handleChange = (content: string, _delta: any, _source: any, editor: any) => {
    if (!input || !input.onChange) return;
    // When editor is emptied, Quill outputs '<p><br></p>'; convert to empty string for form validations
    const isEmpty = editor.getText().trim().length === 0 && !content.includes('<img');
    const sanitizedValue = isEmpty ? '' : content;
    input.onChange(sanitizedValue);
  };

  const handleBlur = () => {
    if (input && input.onBlur) {
      input.onBlur();
    }
  };

  const hasError = Boolean(error && (touched !== undefined ? touched : true));

  const labelTitle = label ? (
    <span>
      <b className={`capital ${className}`}>{label}</b>
    </span>
  ) : null;

  return (
    <Form.Item
      validateStatus={hasError ? 'error' : ''}
      help={hasError ? error : ''}
      label={labelTitle}
      colon={false}
    >
      <QuillWrapper id={idComponent} hasError={hasError} disabled={disabled}>
        <QuillComponent
          theme="snow"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          readOnly={disabled}
          placeholder={placeholder}
          modules={modules}
          formats={formats}
        />
      </QuillWrapper>
    </Form.Item>
  );
};
